/**
 * Solo Gamemaster Store
 *
 * Client-side orchestration for solo play mode.
 * Routes player messages to the AI DM via tRPC,
 * dispatches commands via CommandBridge, manages streaming state.
 */

import { create } from 'zustand';
import { trpc } from '../lib/trpc';
import { useChatStore, type ChatMessage } from './chatStore';
import { useCharacterStore } from './characterStore';
import { useTokenStore } from './tokenStore';
import { executeCommands, getPartyPositions } from '../agents/command-bridge';
import { getDefaultBadge } from '../components/badges';
import type { GamemasterCommand, PendingRollRequest, PresentChoicesCommand, Character } from '@machina/core';
import { useMapStore } from './mapStore';

const CREATE_CHARACTER_BUTTONS = [{ label: 'Create Character', action: 'create_character' }];

// Per-character guard so a portrait is generated at most once in flight while
// it persists to avatarUrl (ensurePartyTokens runs on init AND every action).
const pcPortraitsInFlight = new Set<string>();

/**
 * Build the image-generation descriptor for a PC token from its race + class
 * (+ appearance). The personal NAME is deliberately omitted as the subject —
 * passing "Mr. Finch" to FLUX produced a literal finch. Race/class drives it.
 */
function buildPcTokenDescriptor(char: Character): string {
  const parts: string[] = [];
  if (char.race) parts.push(char.race);
  const cls = char.classes?.[0]?.name;
  if (cls) parts.push(cls);
  const base = parts.length > 0 ? `a ${parts.join(' ')}` : 'a fantasy adventurer';
  // Appearance (skin/hair/build/outfit) front-loads the strongest visual cues.
  return char.appearance ? `${char.appearance}, ${base}` : base;
}

/**
 * For every PC lacking a real image, generate a proper CHARACTER portrait
 * (race/class — not the name) and persist it to the character's avatarUrl so it
 * sticks across reloads and never regenerates. Backfills existing tokens too,
 * so a previously mis-generated token (e.g. the "Mr. Finch" bird) is replaced.
 */
function ensurePcPortraits(characters: Character[]): void {
  for (const char of characters) {
    if (char.tokenImageUrl || char.avatarUrl) continue; // already has a real image
    if (pcPortraitsInFlight.has(char.id)) continue;
    pcPortraitsInFlight.add(char.id);
    void (async () => {
      try {
        const descriptor = buildPcTokenDescriptor(char);
        const result = await trpc.maps.generateToken.mutate({
          creatureName: char.name,
          kind: 'character',
          descriptor,
        });
        // Apply to the PC's token(s) on the board.
        for (const [, token] of useTokenStore.getState().tokens) {
          if (token.ownerId === char.id) {
            useTokenStore.getState().updateToken({ id: token.id, visuals: { imageUrl: result.imageUrl } });
          }
        }
        // Persist to the character (debounced sync) — sticks + no re-gen.
        useCharacterStore.getState().updateCharacter(char.id, { avatarUrl: result.imageUrl });
        console.log(`[SoloGamemaster] Generated character portrait for ${char.name} (${descriptor})`);
      } catch (err) {
        console.warn('[SoloGamemaster] PC portrait generation failed for', char.name, err);
      } finally {
        pcPortraitsInFlight.delete(char.id);
      }
    })();
  }
}

/**
 * Ensure all party characters have tokens on the board, then ensure each has a
 * proper character portrait (generated from race/class, not its name).
 */
function ensurePartyTokens(characters: Character[]): void {
  const tokenStore = useTokenStore.getState();
  const existingOwners = new Set(
    Array.from(tokenStore.tokens.values()).map((t) => t.ownerId).filter(Boolean),
  );

  // Get map dimensions for south-edge placement
  const mapBounds = useMapStore.getState().getMapBoundsInPixels();
  const gridSize = tokenStore.gridSize; // 50px = 5ft
  const mapW = mapBounds?.width ?? 1000;
  const mapH = mapBounds?.height ?? 1000;

  const charsToAdd = characters.filter(c => !existingOwners.has(c.id));
  const positions = getPartyPositions(charsToAdd.length, mapW, mapH, gridSize);

  for (let idx = 0; idx < charsToAdd.length; idx++) {
    const char = charsToAdd[idx];
    const badge = getDefaultBadge(idx);
    tokenStore.addToken({
      name: char.name,
      position: positions[idx] ?? { x: 200 + idx * 80, y: mapH - gridSize * 2 },
      combatantId: char.id,
      ownerId: char.id,
      currentHp: char.currentHp,
      maxHp: char.maxHp,
      ac: char.armorClass ?? 10,
      visuals: {
        shape: 'circle',
        size: 'medium',
        color: badge.accent,
        showHpBar: true,
        showConditionIcons: true,
        label: char.name.slice(0, 2).toUpperCase(),
        // Use an existing custom/saved image if present — else the portrait
        // generated by ensurePcPortraits below will fill it in.
        imageUrl: char.tokenImageUrl || char.avatarUrl || undefined,
      },
    });
  }

  // Generate proper character portraits (race/class) for any PC missing one,
  // including PCs whose token already exists — replaces mis-generated images.
  ensurePcPortraits(characters);
}

interface SoloGamemasterState {
  // Is this campaign running in solo mode?
  isSoloMode: boolean;
  campaignId: string | null;

  // AI DM state
  isProcessing: boolean;
  isInitialized: boolean;
  pendingRoll: PendingRollRequest | null;
  error: string | null;

  // Actions
  setSoloMode: (campaignId: string | null) => void;
  initialize: () => Promise<void>;
  handlePlayerInput: (text: string) => Promise<void>;
  handleRollResult: (notation: string, total: number, natural: number, label: string) => Promise<void>;
  endCombatTurn: () => Promise<void>;
  clearPendingRoll: () => void;
}

export const useSoloGamemasterStore = create<SoloGamemasterState>((set, get) => ({
  isSoloMode: false,
  campaignId: null,
  isProcessing: false,
  isInitialized: false,
  pendingRoll: null,
  error: null,

  setSoloMode: (campaignId) => {
    set({
      isSoloMode: !!campaignId,
      campaignId,
      isInitialized: false,
      pendingRoll: null,
      error: null,
    });
  },

  initialize: async () => {
    const { campaignId, isSoloMode } = get();
    if (!campaignId || !isSoloMode) return;

    set({ isProcessing: true, error: null });

    // Check if player has a character in this campaign (client-side)
    const chars = useCharacterStore.getState().characters;
    const hasCharacter = Array.from(chars.values()).some(
      (c) => c.campaignId === campaignId && !c.isNpc,
    );

    // No character? Show welcome + Create Character button directly (no API call needed)
    if (!hasCharacter) {
      useChatStore.getState().addMessage({
        sender: 'Dungeon Master',
        message: 'Welcome, adventurer! Before we begin, you\'ll need to create a character.',
        type: 'ai_dm',
        choiceButtons: CREATE_CHARACTER_BUTTONS,
      });
      set({ isProcessing: false });
      return;
    }

    try {
      const result = await trpc.solo.initialize.mutate({ campaignId });
      const commands = (result.commands || []) as GamemasterCommand[];

      // Check for choice buttons to embed in the narration message
      const choiceCmd = commands.find((c): c is PresentChoicesCommand => c.type === 'present_choices');

      // Add AI DM narration to chat (with choice buttons if present)
      if (result.narration || choiceCmd) {
        useChatStore.getState().addMessage({
          sender: 'Dungeon Master',
          message: result.narration || '',
          type: 'ai_dm',
          choiceButtons: choiceCmd?.choices,
        });
      }

      // Ensure all PCs have tokens on the board BEFORE executing commands, so
      // the AI's place_party command (which only repositions EXISTING PC
      // tokens) can find them. Previously tokens were created AFTER commands,
      // making place_party a silent no-op on the opening scene — the party
      // only landed at the south-edge fallback.
      const allChars = useCharacterStore.getState().characters;
      const pcChars = Array.from(allChars.values()).filter(
        (c) => c.campaignId === campaignId && !c.isNpc,
      );
      if (pcChars.length > 0) ensurePartyTokens(pcChars);

      // Execute non-choice commands (map generation, scene setup, etc.)
      const otherCommands = commands.filter((c) => c.type !== 'present_choices');
      if (otherCommands.length > 0) {
        await executeCommands(otherCommands, campaignId);
      }

      // Track pending roll if any
      const pendingRoll = result.pendingRoll?.pendingRoll || null;
      if (pendingRoll) {
        addRollRequestToChat(pendingRoll);
      }

      set({ isInitialized: true, isProcessing: false, pendingRoll });
    } catch (err) {
      console.error('[SoloGamemaster] Initialize failed:', err);
      set({ error: (err as Error).message, isProcessing: false });
    }
  },

  handlePlayerInput: async (text) => {
    const { campaignId, isSoloMode, isProcessing } = get();
    if (!campaignId || !isSoloMode) return;
    if (isProcessing) {
      console.warn('[SoloGamemaster] handlePlayerInput skipped — already processing');
      return;
    }

    set({ isProcessing: true, error: null });

    try {
      const result = await trpc.solo.handleAction.mutate({
        campaignId,
        playerInput: text,
      });
      const commands = (result.commands || []) as GamemasterCommand[];

      // Check for choice buttons to embed in the narration message
      const choiceCmd = commands.find((c): c is PresentChoicesCommand => c.type === 'present_choices');

      // Add AI DM narration to chat (with choice buttons if present)
      if (result.narration || choiceCmd) {
        useChatStore.getState().addMessage({
          sender: 'Dungeon Master',
          message: result.narration || '',
          type: 'ai_dm',
          choiceButtons: choiceCmd?.choices,
        });
      }

      // Execute non-choice commands
      const otherCommands = commands.filter((c) => c.type !== 'present_choices');
      if (otherCommands.length > 0) {
        await executeCommands(otherCommands, campaignId);
      }

      // Ensure all PCs have tokens after commands execute (map may have changed)
      const allCharsAfter = useCharacterStore.getState().characters;
      const pcCharsAfter = Array.from(allCharsAfter.values()).filter(
        (c) => c.campaignId === campaignId && !c.isNpc,
      );
      if (pcCharsAfter.length > 0) ensurePartyTokens(pcCharsAfter);

      // Track pending roll
      const pendingRoll = result.pendingRoll?.pendingRoll || null;
      if (pendingRoll) {
        addRollRequestToChat(pendingRoll);
      }

      set({ isProcessing: false, pendingRoll });
    } catch (err) {
      console.error('[SoloGamemaster] Handle action failed:', err);
      set({ error: (err as Error).message, isProcessing: false });
    }
  },

  handleRollResult: async (notation, total, natural, label) => {
    const { campaignId, isSoloMode, isProcessing } = get();
    if (!campaignId || !isSoloMode) return;
    if (isProcessing) {
      console.warn('[SoloGamemaster] handleRollResult skipped — already processing');
      return;
    }

    set({ isProcessing: true, error: null });

    try {
      const result = await trpc.solo.adjudicateRoll.mutate({
        campaignId,
        notation,
        total,
        natural,
        label,
      });

      if (result.narration) {
        useChatStore.getState().addMessage({
          sender: 'Dungeon Master',
          message: result.narration,
          type: 'ai_dm',
        });
      }

      if (result.commands.length > 0) {
        await executeCommands(result.commands as GamemasterCommand[], campaignId);
      }

      const pendingRoll = result.pendingRoll?.pendingRoll || null;
      if (pendingRoll) {
        addRollRequestToChat(pendingRoll);
      }

      set({ isProcessing: false, pendingRoll });
    } catch (err) {
      console.error('[SoloGamemaster] Adjudicate roll failed:', err);
      set({ error: (err as Error).message, isProcessing: false });
    }
  },

  endCombatTurn: async () => {
    const { campaignId, isSoloMode, isProcessing } = get();
    if (!campaignId || !isSoloMode) return;
    if (isProcessing) {
      console.warn('[SoloGamemaster] endCombatTurn skipped — already processing');
      return;
    }

    set({ isProcessing: true, error: null });

    try {
      const result = await trpc.solo.endCombatTurn.mutate({ campaignId });

      if (result.narration) {
        useChatStore.getState().addMessage({
          sender: 'Dungeon Master',
          message: result.narration,
          type: 'ai_dm',
        });
      }

      if (result.commands.length > 0) {
        await executeCommands(result.commands as GamemasterCommand[], campaignId);
      }

      const pendingRoll = result.pendingRoll?.pendingRoll || null;
      if (pendingRoll) {
        addRollRequestToChat(pendingRoll);
      }

      set({ isProcessing: false, pendingRoll });
    } catch (err) {
      console.error('[SoloGamemaster] End combat turn failed:', err);
      set({ error: (err as Error).message, isProcessing: false });
    }
  },

  clearPendingRoll: () => set({ pendingRoll: null }),
}));

/**
 * Add a roll request card to chat
 */
function addRollRequestToChat(roll: PendingRollRequest): void {
  const skillOrAbility = roll.skill || roll.ability || '';
  const typeLabel = roll.rollType === 'saving_throw' ? 'Saving Throw' :
    roll.rollType === 'skill_check' ? 'Skill Check' :
    roll.rollType === 'ability_check' ? 'Ability Check' :
    roll.rollType === 'attack' ? 'Attack Roll' : 'Roll';

  const msg: Omit<ChatMessage, 'id' | 'timestamp'> = {
    sender: 'Dungeon Master',
    message: `${typeLabel}: ${skillOrAbility} — ${roll.reason}`,
    type: 'roll_request',
    rollRequestData: {
      id: roll.id,
      rollType: roll.rollType,
      ability: roll.ability,
      skill: roll.skill,
      dc: roll.dc,
      reason: roll.reason,
      hideDc: roll.hideDc,
      resolved: false,
    },
  };
  useChatStore.getState().addMessage(msg);
}
